import { useState } from 'react';
import { Button, Card, Alert, Spinner, Form, Row, Col, Badge, ProgressBar } from 'react-bootstrap';
import { FaSync, FaCheck, FaTimes, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import AdminLayout from '../../components/admin/AdminLayout';
import { motion } from 'framer-motion';

const DataSync = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [region, setRegion] = useState('IN');
  const [page, setPage] = useState(1);
  const [language, setLanguage] = useState('');
  const [genre, setGenre] = useState('');
  const [search, setSearch] = useState('');
  const [fetchedMovies, setFetchedMovies] = useState([]);
  const [syncResults, setSyncResults] = useState(null);
  const [progress, setProgress] = useState(0);

  const getApiUrl = () => {
    return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  };

  const handleFetchMovies = async () => {
    try {
      setLoading(true);
      const apiUrl = getApiUrl();
      
      const response = await axios.get(`${apiUrl}/sync/fetch-tmdb-movies`, {
        params: {
          region,
          page,
          language: language || undefined,
          genre: genre || undefined,
          search: search || undefined
        }
      });

      setFetchedMovies(response.data.movies || []);
      toast.success(`Fetched ${response.data.movies?.length || 0} movies`);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch movies: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMovies = async (moviesToAdd) => {
    try {
      setSyncing(true);
      const apiUrl = getApiUrl();
      
      const response = await axios.post(`${apiUrl}/sync/add-movies`, {
        movies: moviesToAdd
      });

      setSyncResults({
        timestamp: new Date().toLocaleString(),
        added: response.data.added || 0,
        status: 'success'
      });
      
      setFetchedMovies(fetchedMovies.filter(m => !moviesToAdd.find(a => a.id === m.id)));
      toast.success(`Added ${response.data.added} movies`);
    } catch (error) {
      console.error('Add error:', error);
      toast.error('Failed to add movies: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleRemoveMovie = (movieId) => {
    setFetchedMovies(fetchedMovies.filter(m => m.id !== movieId));
  };

  const successRate = syncResults && syncResults.added > 0 
    ? 100
    : 0;

  return (
    <AdminLayout>
      <div style={{ background: '#1f2025', minHeight: '100vh', color: '#fff', padding: '20px 15px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-4">
            <h1 className="text-white mb-2" style={{ fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}>
              <FaDownload className="me-2" />TMDB Movie Sync
            </h1>
            <p className="text-light mb-0">Synchronize movies from TMDB database</p>
            <Button
              size="sm"
              onClick={async () => {
                try {
                  const response = await axios.get(`${getApiUrl()}/sync/sync-tmdb-movies?region=IN&page=1`);
                  toast.success(`Synced ${response.data.total} movies with backdrops`);
                } catch (error) {
                  toast.error('Failed to sync movies');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, rgb(230, 57, 70), rgb(248, 69, 101))',
                borderRadius: '8px',
                border: 'none',
                fontWeight: '600',
                fontSize: '13px',
                padding: '6px 12px',
                marginTop: '10px'
              }}
            >
              🎬 Sync All Movies
            </Button>
          </div>

          <Row className="mb-4 g-3">
            <Col lg={8}>
              <Card className="border-0" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
                <Card.Body className="p-3 p-md-4">
                  <h5 className="text-white mb-4">Sync Configuration</h5>
                  
                  <Form>
                    <Row className="g-2 g-md-3">
                      <Col xs={12} md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-bold">Search Movie</Form.Label>
                          <Form.Control 
                            type="text" 
                            placeholder="e.g., Mahasenha"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            disabled={syncing}
                            style={{ background: 'rgba(255, 255, 255, 0.05)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '10px' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-bold">Region</Form.Label>
                          <Form.Select 
                            value={region} 
                            onChange={(e) => setRegion(e.target.value)}
                            disabled={syncing}
                            style={{ background: 'rgba(255, 255, 255, 0.05)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '10px' }}
                          >
                            <option value="IN">🇮🇳 India</option>
                            <option value="US">🇺🇸 United States</option>
                            <option value="GB">🇬🇧 United Kingdom</option>
                            <option value="CA">🇨🇦 Canada</option>
                            <option value="AU">🇦🇺 Australia</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-bold">Language</Form.Label>
                          <Form.Select 
                            value={language} 
                            onChange={(e) => setLanguage(e.target.value)}
                            disabled={syncing}
                            style={{ background: 'rgba(255, 255, 255, 0.05)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '10px' }}
                          >
                            <option value="">All Languages</option>
                            <option value="en">English</option>
                            <option value="hi">Hindi</option>
                            <option value="ta">Tamil</option>
                            <option value="te">Telugu</option>
                            <option value="kn">Kannada</option>
                            <option value="ml">Malayalam</option>
                            <option value="mr">Marathi</option>
                            <option value="gu">Gujarati</option>
                            <option value="bn">Bengali</option>
                            <option value="pa">Punjabi</option>
                            <option value="ur">Urdu</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                            <option value="de">German</option>
                            <option value="it">Italian</option>
                            <option value="pt">Portuguese</option>
                            <option value="ru">Russian</option>
                            <option value="ja">Japanese</option>
                            <option value="zh">Chinese</option>
                            <option value="ko">Korean</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-bold">Genre</Form.Label>
                          <Form.Select 
                            value={genre} 
                            onChange={(e) => setGenre(e.target.value)}
                            disabled={syncing}
                            style={{ background: 'rgba(255, 255, 255, 0.05)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '10px' }}
                          >
                            <option value="">All Genres</option>
                            <option value="28">Action</option>
                            <option value="12">Adventure</option>
                            <option value="16">Animation</option>
                            <option value="35">Comedy</option>
                            <option value="80">Crime</option>
                            <option value="18">Drama</option>
                            <option value="27">Horror</option>
                            <option value="10749">Romance</option>
                            <option value="878">Sci-Fi</option>
                            <option value="53">Thriller</option>
                            <option value="10752">War</option>
                            <option value="37">Western</option>
                            <option value="10402">Music</option>
                            <option value="10770">TV Movie</option>
                            <option value="9648">Mystery</option>
                            <option value="10751">Family</option>
                            <option value="14">Fantasy</option>
                            <option value="36">History</option>
                            <option value="99">Documentary</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white fw-bold">Page</Form.Label>
                          <Form.Control 
                            type="number" 
                            value={page}
                            onChange={(e) => setPage(parseInt(e.target.value) || 1)}
                            disabled={syncing}
                            min="1"
                            style={{ background: 'rgba(255, 255, 255, 0.05)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '10px' }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Button 
                      onClick={handleFetchMovies}
                      disabled={syncing || loading}
                      size="lg"
                      className="w-100"
                      style={{
                        background: 'linear-gradient(135deg, #e63946, #f84565)',
                        border: 'none',
                        borderRadius: '10px',
                        fontWeight: '600'
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Fetching...
                        </>
                      ) : (
                        <>
                          <FaSync className="me-2" />
                          Fetch Movies
                        </>
                      )}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {fetchedMovies.length > 0 && (
            <Card className="border-0 mb-4" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Card.Body className="p-3 p-md-4">
                <h5 className="text-white mb-4">Fetched Movies ({fetchedMovies.length})</h5>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-dark table-hover" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ color: '#fff' }}>Title</th>
                        <th style={{ color: '#fff' }}>Release Date</th>
                        <th style={{ color: '#fff' }}>Rating</th>
                        <th style={{ color: '#fff' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fetchedMovies.map((movie) => (
                        <tr key={movie.id}>
                          <td style={{ color: '#fff' }}>{movie.title}</td>
                          <td style={{ color: '#fff' }}>{movie.releaseDate || 'N/A'}</td>
                          <td style={{ color: '#fff' }}>{movie.rating || 'N/A'}</td>
                          <td>
                            <Button 
                              size="sm" 
                              variant="outline-danger"
                              onClick={() => handleRemoveMovie(movie.id)}
                              className="me-2"
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button 
                  onClick={() => handleAddMovies(fetchedMovies)}
                  disabled={syncing}
                  size="lg"
                  className="w-100 mt-3"
                  style={{
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600'
                  }}
                >
                  {syncing ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Adding...
                    </>
                  ) : (
                    <>Add All Movies</>
                  )}
                </Button>
              </Card.Body>
            </Card>
          )}

          {syncResults && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {syncResults.status === 'success' ? (
                <Card className="border-0 mb-4" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
                  <Card.Body className="p-3 p-md-4">
                    <div className="d-flex align-items-center mb-4">
                      <FaCheck className="text-success me-3" size={24} />
                      <div>
                        <h5 className="text-white mb-0">Movies Added Successfully</h5>
                        <small className="text-light">{syncResults.timestamp}</small>
                      </div>
                    </div>

                    <Row className="mb-4 g-2 g-md-3">
                      <Col xs={12} sm={6} md={3}>
                        <div className="p-3 rounded" style={{ background: 'rgba(40, 167, 69, 0.15)', border: '1px solid rgba(40, 167, 69, 0.5)' }}>
                          <p className="mb-1 text-light"><strong>✅ Added</strong></p>
                          <h4 className="text-success mb-0">{syncResults.added}</h4>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ) : (
                <Alert variant="danger" className="mb-4">
                  <FaTimes className="me-2" />
                  <strong>Failed:</strong> {syncResults.error}
                </Alert>
              )}
            </motion.div>
          )}

        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default DataSync;
